import { useCallback, useState } from 'react';

import { useKnowledgeEnabled } from '~/components/knowledge/useKnowledgeEnabled';
import { PublishKnowledgeDialog } from '~/components/tasks/detail/PublishKnowledgeDialog';
import type { TaskDetail } from '~/data/generated/model';

/**
 * Completions wait their turn: a second one arriving while a prompt is open
 * opens after it closes instead of replacing it.
 */
export function useCompletionPrompts() {
  const knowledgeEnabled = useKnowledgeEnabled();
  const [queue, setQueue] = useState<TaskDetail[]>([]);
  const add = useCallback(
    (task: TaskDetail) => {
      if (!knowledgeEnabled) return;
      setQueue((q) =>
        q.some((t) => t.id === task.id)
          ? q.map((t) => (t.id === task.id ? task : t))
          : [...q, task],
      );
    },
    [knowledgeEnabled],
  );
  // By id: a prompt can close twice (Cancel while its save is in flight), and the
  // second close must not take the next prompt with it.
  const close = useCallback(
    (taskId: string) => setQueue((q) => q.filter((t) => t.id !== taskId)),
    [],
  );
  return { task: queue[0] ?? null, add, close };
}

export function CompletionPrompt({
  task,
  onClose,
}: {
  task: TaskDetail | null;
  onClose: (taskId: string) => void;
}) {
  const knowledgeEnabled = useKnowledgeEnabled();
  if (!knowledgeEnabled || !task) return null;
  // Keyed so the next task's prompt starts with its own drafts, never the last one's.
  return (
    <PublishKnowledgeDialog
      key={task.id}
      task={task}
      mode="complete"
      onClose={() => onClose(task.id)}
    />
  );
}
